import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { usePlatformSettings } from '../hooks/usePlatformSettings';

const TermsOfService = () => {
  const navigate = useNavigate();
  const { settings: platformSettings } = usePlatformSettings();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', py: 8 }}>
      <Container maxWidth="md">
        <Box 
          onClick={() => navigate('/')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 4, 
            cursor: 'pointer',
            color: '#00FFF5',
            '&:hover': { color: '#9D00FF' },
            transition: 'color 0.3s'
          }}
        >
          <BackIcon sx={{ mr: 1 }} />
          <Typography>Back to Home</Typography>
        </Box>

        <Paper sx={{ 
          p: 5, 
          bgcolor: '#171717', 
          border: '1px solid #262626',
          borderRadius: '12px'
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 2, 
              fontWeight: 700,
              background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Terms of Service
          </Typography>

          <Typography variant="body2" sx={{ mb: 4, color: '#737373' }}>
            Last Updated: November 14, 2025
          </Typography>

          {/* Acceptance */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            By accessing or using {platformSettings.platformName}, you agree to be bound by these Terms of Service 
            and our Privacy Policy. If you do not agree to these terms, please do not use our service.
          </Typography>

          {/* Service Description */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            2. Service Description
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            {platformSettings.platformName} is a cognitive profiling service that analyzes your conversation patterns 
            to generate insights about your thinking style, creativity, and cognitive patterns. Our service uses AI to 
            process your chat logs (1,000-50,000 words) and produces a comprehensive report including:
            <br />
            • Cognitive Efficiency Score (IQ proxy)
            <br />
            • Creativity Quotient with 4 sub-dimensions
            <br />
            • Cognitive Tier classification
            <br />
            • Behavioral patterns and blind spots
            <br />
            • 9-section professional PDF report
          </Typography>

          {/* User Responsibilities */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            3. User Responsibilities
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            You agree to:
            <br />
            • Provide accurate information during account creation
            <br />
            • Use the service only for lawful purposes
            <br />
            • Upload only your own chat logs or content you have permission to use
            <br />
            • Not attempt to reverse-engineer, hack, or misuse the platform
            <br />
            • Keep your account credentials secure
            <br />
            • Not share your account with others
            <br />
            • Be at least 18 years of age
          </Typography>

          {/* Content Ownership */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            4. Content Ownership and License
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#00FFF5', fontWeight: 500 }}>
            4.1 Your Content
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            You retain all rights to the chat logs and data you upload. By uploading content, you grant us a 
            temporary, limited license to process your content solely for generating your cognitive profile report.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#00FFF5', fontWeight: 500 }}>
            4.2 Generated Reports
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            The reports generated by our AI belong to you. You may download, share, or delete them at any time. 
            We do not claim ownership of your reports.
          </Typography>

          {/* Payment Terms */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            5. Payment Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            • Pricing is displayed before purchase and may vary based on beta/promotional status
            <br />
            • All payments are processed securely through Stripe
            <br />
            • Prices are in USD unless otherwise specified
            <br />
            • Payment is required before report generation
            <br />
            • One free regeneration is included within 30 days of purchase
          </Typography>

          {/* Refund Policy */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            6. Refund Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            <strong>Standard Policy:</strong> Due to the immediate delivery of digital reports, all sales are final once 
            your report is generated.
            <br /><br />
            <strong>Exceptions:</strong> Refunds may be issued at our discretion in cases of:
            <br />
            • Technical failure preventing report generation
            <br />
            • Payment processing errors resulting in duplicate charges
            <br />
            • Service unavailability for extended periods
            <br /><br />
            To request a refund, contact us through the in-app messaging system (Inbox) within 7 days of purchase.
          </Typography>

          {/* Service Limitations */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            7. Service Limitations and Disclaimers
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            <strong>NOT A MEDICAL OR PSYCHOLOGICAL SERVICE:</strong>
            <br />
            • Our reports are for informational and entertainment purposes only
            <br />
            • This is NOT a clinical assessment, diagnosis, or treatment
            <br />
            • Do NOT use for employment, legal, medical, or educational decisions
            <br />
            • Results are AI-generated estimates, not validated psychometric measurements
            <br />
            • Consult licensed professionals for clinical assessments
            <br /><br />
            <strong>Accuracy:</strong>
            <br />
            • AI analysis may not be 100% accurate
            <br />
            • Results depend on the quality and quantity of input data
            <br />
            • Reports reflect patterns at the time of analysis, not permanent traits
          </Typography>

          {/* Data Privacy */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            8. Data Privacy and Security
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            • Your chat logs are temporarily stored for processing and one-time regeneration
            <br />
            • Chat logs are automatically deleted after 30 days
            <br />
            • You can manually delete your data at any time
            <br />
            • We do not sell or share your personal data
            <br />
            • See our Privacy Policy for full details
          </Typography>

          {/* Intellectual Property */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            9. Intellectual Property
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            The {platformSettings.platformName} platform, including its design, code, algorithms, branding, and content 
            (excluding user-generated content) is owned by {platformSettings.companyName || 'NeurolNet Inc.'} and protected 
            by copyright, trademark, and other intellectual property laws.
            <br /><br />
            You may not:
            <br />
            • Copy, modify, or distribute our platform code or algorithms
            <br />
            • Use our branding or trademarks without permission
            <br />
            • Scrape or data mine our platform
            <br />
            • Create derivative works based on our service
          </Typography>

          {/* Prohibited Use */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            10. Prohibited Use
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            You may not use our service to:
            <br />
            • Analyze chat logs without the consent of all participants
            <br />
            • Upload illegal, harmful, or offensive content
            <br />
            • Harass, abuse, or harm others
            <br />
            • Violate any applicable laws or regulations
            <br />
            • Attempt to overwhelm our servers or disrupt service
            <br />
            • Use automated bots or scripts without permission
            <br />
            • Make employment, legal, or medical decisions based on reports
          </Typography>

          {/* Account Termination */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            11. Account Termination
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We reserve the right to suspend or terminate your account if you:
            <br />
            • Violate these Terms of Service
            <br />
            • Engage in fraudulent activity
            <br />
            • Misuse the platform or disrupt service
            <br />
            • Fail to pay for services
            <br /><br />
            You may delete your account at any time from your user dashboard. Deletion is permanent and irreversible.
          </Typography>

          {/* Limitation of Liability */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            12. Limitation of Liability
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            <br /><br />
            {platformSettings.companyName || 'NeurolNet Inc.'} IS NOT LIABLE FOR:
            <br />
            • Any indirect, incidental, special, or consequential damages
            <br />
            • Loss of profits, data, or business opportunities
            <br />
            • Decisions made based on report content
            <br />
            • Service interruptions or technical failures
            <br />
            • Inaccuracies in AI-generated reports
            <br /><br />
            OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE.
          </Typography>

          {/* Warranty Disclaimer */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            13. Warranty Disclaimer
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT 
            LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            <br /><br />
            We do not guarantee:
            <br />
            • Uninterrupted or error-free service
            <br />
            • Accuracy of AI-generated content
            <br />
            • Specific results or outcomes
            <br />
            • Compatibility with all devices or browsers
          </Typography>

          {/* Indemnification */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            14. Indemnification
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            You agree to indemnify and hold harmless {platformSettings.companyName || 'NeurolNet Inc.'}, its officers, 
            directors, employees, and agents from any claims, damages, losses, or expenses arising from:
            <br />
            • Your use of the service
            <br />
            • Your violation of these terms
            <br />
            • Your violation of any third-party rights
            <br />
            • Content you upload or share
          </Typography>

          {/* Changes to Terms */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            15. Changes to Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We may update these Terms of Service at any time. We will notify users of significant changes via email 
            or platform notice. Your continued use after changes constitutes acceptance of the new terms.
          </Typography>

          {/* Governing Law */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            16. Governing Law and Disputes
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            These terms are governed by the laws of the United States and the State of [Your State].
            <br /><br />
            Any disputes shall be resolved through:
            <br />
            1. Good faith negotiation
            <br />
            2. Mediation (if negotiation fails)
            <br />
            3. Binding arbitration in [Your Jurisdiction]
            <br /><br />
            You waive the right to participate in class-action lawsuits against us.
          </Typography>

          {/* Severability */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            17. Severability
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            If any provision of these terms is found to be unenforceable, the remaining provisions shall remain in full effect.
          </Typography>

          {/* Contact */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            18. Contact Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            For questions about these Terms of Service, contact us:
            <br /><br />
            <strong>Support:</strong> Use the in-app messaging system (click "Contact Support" in the footer or visit your Inbox)
            <br />
            <strong>Website:</strong> https://neurolnet.ai
            <br />
            <strong>Company:</strong> {platformSettings.companyName || 'NeurolNet Inc.'}
            <br /><br />
            Our AI support agent is available 24/7 to answer your questions.
          </Typography>

          {/* Acknowledgment */}
          <Box sx={{ 
            mt: 5, 
            p: 3, 
            bgcolor: '#0a0a0a', 
            border: '1px solid #00FFF5',
            borderRadius: '8px'
          }}>
            <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.8 }}>
              <strong style={{ color: '#00FFF5' }}>By using {platformSettings.platformName}, you acknowledge that:</strong>
              <br />
              • You have read and understood these Terms of Service
              <br />
              • You agree to be bound by these terms
              <br />
              • This is not a medical, psychological, or clinical service
              <br />
              • Reports are for informational purposes only
              <br />
              • You are at least 18 years of age
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;

